import { useMemo, useState } from 'react';

import { OpportunityStatus, OpportunityView } from '@ndvr/customer-api';

import { formatCurrencyShort } from 'utils/number-formatter';

import { OpportunityFunnelDrawer } from './OpportunityFunnelDrawer';

interface OpportunityFunnelViewProps {
    opportunities: OpportunityView[];
    householdMap: Map<string, { name: string }>;
    isLoading?: boolean;
}

interface FunnelStage {
    status: OpportunityStatus;
    label: string;
    value: number;
    count: number;
}

const PIPELINE_STAGES = [
    { status: OpportunityStatus.IDENTIFIED, label: 'Identified' },
    { status: OpportunityStatus.INFO_GATHERING, label: 'Info Gathering' },
    { status: OpportunityStatus.RECOMMENDATION, label: 'Recommendation' },
    { status: OpportunityStatus.DECISION, label: 'Decision' },
    { status: OpportunityStatus.ONBOARDING, label: 'Onboarding' },
];

const STAGE_FILLS = [
    'hsl(210, 50%, 62%)',
    'hsl(210, 50%, 54%)',
    'hsl(210, 50%, 47%)',
    'hsl(210, 50%, 40%)',
    'hsl(210, 50%, 28%)',
];

export const OpportunityFunnelView = (props: OpportunityFunnelViewProps) => {
    const [selectedStage, setSelectedStage] = useState<{
        label: string;
        opportunities: OpportunityView[];
    } | null>(null);

    const pipelineData = useMemo(() => {
        return PIPELINE_STAGES.map((stage) => {
            const stageOpps = props.opportunities.filter((opp) => opp.status === stage.status);
            const count = stageOpps.length;
            const totalValue = stageOpps.reduce((sum, opp) => sum + (Number(opp.value) || 0), 0);
            return {
                ...stage,
                count,
                value: totalValue,
                opportunities: stageOpps,
            };
        });
    }, [props.opportunities]);

    const handleStageClick = (stage: { label: string; opportunities: OpportunityView[] }) => {
        if (stage.opportunities.length === 0) return;
        setSelectedStage(stage);
    };

    const closedWonData = useMemo(() => {
        const wonOpps = props.opportunities.filter((opp) => opp.status === OpportunityStatus.CLOSED_WON);
        return {
            count: wonOpps.length,
            value: wonOpps.reduce((sum, opp) => sum + (Number(opp.value) || 0), 0),
            opportunities: wonOpps,
        };
    }, [props.opportunities]);

    const closedLostData = useMemo(() => {
        const lostOpps = props.opportunities.filter((opp) => opp.status === OpportunityStatus.CLOSED_LOST);
        return {
            count: lostOpps.length,
            value: lostOpps.reduce((sum, opp) => sum + (Number(opp.value) || 0), 0),
            opportunities: lostOpps,
        };
    }, [props.opportunities]);

    const totalOpportunities =
        pipelineData.reduce((sum, s) => sum + s.count, 0) + closedWonData.count + closedLostData.count;
    const totalPipelineValue =
        pipelineData.reduce((sum, s) => sum + s.value, 0) + closedWonData.value + closedLostData.value;

    if (props.isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading funnel data...</div>
            </div>
        );
    }

    const svgWidth = 520;
    const segmentHeight = 52;
    const segmentGap = 0;
    const numStages = pipelineData.length;
    const totalHeight = numStages * segmentHeight + 24;
    const centerX = svgWidth / 2;
    const startY = 12;

    const maxWidthPercent = 0.95;
    const minWidthPercent = 0.2;
    const widthStep = (maxWidthPercent - minWidthPercent) / (numStages - 1);

    const widthPercentages = pipelineData.map((_, index) => {
        return maxWidthPercent - index * widthStep;
    });

    const generateSegmentPath = (topWidth: number, bottomWidth: number, yOffset: number, height: number) => {
        const topLeft = centerX - (svgWidth * topWidth) / 2;
        const topRight = centerX + (svgWidth * topWidth) / 2;
        const bottomLeft = centerX - (svgWidth * bottomWidth) / 2;
        const bottomRight = centerX + (svgWidth * bottomWidth) / 2;

        return `
      M ${topLeft} ${yOffset}
      L ${topRight} ${yOffset}
      L ${bottomRight} ${yOffset + height}
      L ${bottomLeft} ${yOffset + height}
      Z
    `;
    };

    return (
        <div className="rounded-lg border bg-white p-8 shadow-sm">
            <div className="mb-6 text-center">
                <h2 className="text-2xl font-semibold">Opportunity Funnel</h2>
                <p className="text-muted-foreground mt-1 text-sm">Active pipeline by stage</p>
            </div>

            <div className="flex justify-center">
                <svg
                    width={svgWidth}
                    height={totalHeight}
                    viewBox={`0 0 ${svgWidth} ${totalHeight}`}
                    className="h-auto max-w-full"
                >
                    {pipelineData.map((stage, index) => {
                        const topWidth = widthPercentages[index];
                        const bottomWidth = widthPercentages[index + 1] ?? minWidthPercent * 0.75;
                        const yOffset = startY + index * (segmentHeight + segmentGap);
                        const path = generateSegmentPath(topWidth, bottomWidth, yOffset, segmentHeight);
                        const textCenterY = yOffset + segmentHeight / 2;
                        const isDisabled = stage.count === 0;

                        return (
                            <g
                                key={stage.status}
                                onClick={() => handleStageClick(stage)}
                                className={isDisabled ? 'cursor-not-allowed opacity-35' : 'cursor-pointer'}
                            >
                                <path
                                    d={path}
                                    fill={STAGE_FILLS[index]}
                                    stroke="hsl(var(--background))"
                                    strokeWidth={1}
                                    opacity={isDisabled ? 0.35 : 1}
                                />
                                <g className="pointer-events-none">
                                    <text
                                        x={centerX}
                                        y={textCenterY - 8}
                                        dominantBaseline="middle"
                                        textAnchor="middle"
                                        fill="white"
                                        fontWeight="600"
                                        fontSize="12"
                                        letterSpacing="0.02em"
                                    >
                                        {stage.label}
                                    </text>
                                    <text
                                        x={centerX}
                                        y={textCenterY + 8}
                                        dominantBaseline="middle"
                                        textAnchor="middle"
                                        fill="white"
                                        fontWeight="700"
                                        fontSize="14"
                                    >
                                        {formatCurrencyShort(stage.value)}
                                    </text>
                                    <text
                                        x={centerX}
                                        y={textCenterY + 22}
                                        dominantBaseline="middle"
                                        textAnchor="middle"
                                        fill="white"
                                        fontWeight="400"
                                        fontSize="10"
                                        opacity="0.7"
                                    >
                                        {stage.count} {stage.count === 1 ? 'opp' : 'opps'}
                                    </text>
                                </g>
                            </g>
                        );
                    })}
                </svg>
            </div>

            <p className="text-muted-foreground mt-6 text-center text-xs italic">
                Funnel width reflects total expected assets by stage.
            </p>

            <div className="mt-8 space-y-4">
                <div
                    className={`border-primary/30 bg-primary/5 rounded-lg border-2 border-dashed p-4 ${closedWonData.count > 0 ? 'hover:bg-primary/10 cursor-pointer transition-colors' : 'opacity-50'}`}
                    onClick={() =>
                        closedWonData.count > 0 &&
                        handleStageClick({ label: 'Closed Won', opportunities: closedWonData.opportunities })
                    }
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <svg className="text-primary h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                            </svg>
                            <span className="text-foreground font-semibold">Closed Won</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-right">
                                <p className="text-primary text-xl font-bold">
                                    {formatCurrencyShort(closedWonData.value)}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                    {closedWonData.count} {closedWonData.count === 1 ? 'opportunity' : 'opportunities'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={`border-destructive/30 bg-destructive/5 rounded-lg border-2 border-dashed p-4 ${closedLostData.count > 0 ? 'hover:bg-destructive/10 cursor-pointer transition-colors' : 'opacity-50'}`}
                    onClick={() =>
                        closedLostData.count > 0 &&
                        handleStageClick({ label: 'Closed Lost', opportunities: closedLostData.opportunities })
                    }
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <svg
                                className="text-destructive h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 9l-6 6m0-6l6 6"
                                />
                            </svg>
                            <span className="text-foreground font-semibold">Closed Lost</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-right">
                                <p className="text-destructive text-xl font-bold">
                                    {formatCurrencyShort(closedLostData.value)}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                    {closedLostData.count}{' '}
                                    {closedLostData.count === 1 ? 'opportunity' : 'opportunities'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-border mt-8 flex justify-center gap-12 border-t pt-6">
                <div className="text-center">
                    <p className="text-foreground text-2xl font-semibold">{totalOpportunities}</p>
                    <p className="text-muted-foreground text-sm">Total Opportunities</p>
                </div>
                <div className="text-center">
                    <p className="text-foreground text-2xl font-semibold">{formatCurrencyShort(totalPipelineValue)}</p>
                    <p className="text-muted-foreground text-sm">Total Pipeline Value</p>
                </div>
            </div>

            <OpportunityFunnelDrawer
                isOpen={!!selectedStage}
                onClose={() => setSelectedStage(null)}
                stageLabel={selectedStage?.label || ''}
                opportunities={selectedStage?.opportunities || []}
                householdMap={props.householdMap}
            />
        </div>
    );
};
